/**
 * Base API Client
 *
 * Platform-agnostic base implementation of ApiClient.
 * Handles common logic for all platforms (Browser, SSR, React Native).
 */

import { AuthRequiredError, isPrivateEndpoint } from './authUtils';
import {
  ApiError,
  type ApiClient,
  type ApiClientConfig,
  type RequestConfig,
} from './client';
import type { ApiResponse } from './types';

/**
 * Data key mapping for different services
 * Maps service name to the key used for data in API responses
 *
 * Note: NTS API is inconsistent - some endpoints use 'result', some use 'data'
 * The parseResponse method will automatically fallback to alternative keys
 */
const SERVICE_DATA_KEY_MAP: Record<string, string> = {
  // OEX and BFF use 'result'
  oex: 'result',
  bff: 'result',
  // NTS is inconsistent, but 'result' is more common, with automatic fallback to 'data'
  nts: 'result',
  // BE uses 'data'
  be: 'data',
  // Future services can be added here
  // ma: 'res',
};

/**
 * Get data key for a service
 * Returns 'result' by default for unknown services
 */
function getDataKeyForService(service?: string): string {
  if (!service) return 'result';
  return SERVICE_DATA_KEY_MAP[service] || 'result';
}

/**
 * Sub-account header configuration
 */
export interface SubAccountConfig {
  /**
   * Get sub-account UUID from storage
   * Returns empty string for master account
   */
  getSubAccountUUID?: () => string | undefined;

  /**
   * Map of API paths that should include sub-account header
   * Key is the API path (without version prefix), value is true if enabled
   */
  enabledApis?: Record<string, boolean>;

  /**
   * Header name for sub-account
   * @default 'sub-account'
   */
  headerName?: string;
}

/**
 * Base API Client configuration
 */
export interface BaseApiClientConfig extends ApiClientConfig {
  /**
   * Get authentication token
   * Can be sync or async depending on platform
   */
  getToken?: () => string | undefined | Promise<string | undefined>;

  /**
   * Handle error responses
   * Platform-specific error handling (e.g., show toast in browser)
   */
  onError?: (error: ApiError) => void | Promise<void>;

  /**
   * Service name (used to determine data key mapping)
   */
  service?: string;

  /**
   * Sub-account configuration for adding sub-account header to requests
   */
  subAccount?: SubAccountConfig;
}

/**
 * Build full URL with query parameters
 */
export function buildUrl(
  baseUrl: string,
  url: string,
  params?: Record<string, any>,
): string {
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  if (!params || Object.keys(params).length === 0) {
    return fullUrl;
  }

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `${fullUrl}?${queryString}` : fullUrl;
}

/**
 * Check if an API path should include sub-account header
 * @param url - The API URL path
 * @param enabledApis - Map of enabled API paths
 */
function shouldAddSubAccountHeader(
  url: string,
  enabledApis?: Record<string, boolean>,
): boolean {
  if (!enabledApis) return false;

  // Extract the API path without version prefix (e.g., '/v1/order/create' -> 'order/create')
  const urlPath = url.replace(/^\/(v\d\/)?/, '').split('?')[0];

  return urlPath ? !!enabledApis[urlPath] : false;
}

/**
 * Build request headers with optional token and sub-account header
 */
export async function buildHeaders(
  defaultHeaders: Record<string, string>,
  config: RequestConfig | undefined,
  getToken?: () => string | undefined | Promise<string | undefined>,
  subAccountConfig?: SubAccountConfig,
  url?: string,
): Promise<Record<string, string>> {
  // Detect if body is FormData
  const isFormData = config?.body instanceof FormData;

  const headers: Record<string, string> = {
    ...(!isFormData && { 'Content-Type': 'application/json' }),
    ...defaultHeaders,
    ...config?.headers,
  };

  // Add token if withToken is true (default)
  const withToken = config?.withToken !== false;
  if (withToken && getToken) {
    const token = await getToken();
    if (token) {
      headers['exchange-token'] = token;
    }
  }

  // Add sub-account header if configured and the API is enabled
  if (
    url &&
    subAccountConfig?.getSubAccountUUID &&
    shouldAddSubAccountHeader(url, subAccountConfig.enabledApis)
  ) {
    const subAccountUUID = subAccountConfig.getSubAccountUUID();
    if (subAccountUUID) {
      const headerName = subAccountConfig.headerName || 'sub-account';
      headers[headerName] = subAccountUUID;
    }
  }

  return headers;
}

/**
 * Create ApiError from unknown error
 */
function createApiError(error: unknown, defaultCode = 500): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (error instanceof Error) {
    if (error.name === 'AbortError') {
      return new ApiError(408, 'Request timeout');
    }
    return new ApiError(defaultCode, error.message);
  }

  return new ApiError(defaultCode, 'Unknown error');
}

/**
 * Base API Client implementation
 * Contains common logic shared across all platforms
 */
export class BaseApiClient implements ApiClient {
  protected baseUrl: string;
  protected defaultHeaders: Record<string, string>;
  protected defaultTimeout: number;
  protected getToken?: () => string | undefined | Promise<string | undefined>;
  protected onError?: (error: ApiError) => void | Promise<void>;
  protected service?: string;
  protected dataKey: string;
  protected subAccountConfig?: SubAccountConfig;

  constructor(config: BaseApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.defaultHeaders = config.headers || {};
    this.defaultTimeout = config.timeout || 30000;
    this.getToken = config.getToken;
    this.onError = config.onError;
    this.service = config.service;
    this.dataKey = getDataKeyForService(config.service);
    this.subAccountConfig = config.subAccount;
  }

  /**
   * Parse and validate response data
   *
   * @param response - The fetch Response object
   * @param data - The parsed JSON data
   * @param config - Request configuration
   */
  private parseResponse<T>(
    response: Response,
    data: unknown,
    config?: RequestConfig,
  ): ApiResponse<T> {
    const httpStatus = response.status;

    // Raw response mode - skip extracting from 'result' field
    // Use the entire response body as data
    if (config?.rawResponse) {
      return {
        code: response.ok ? 0 : response.status,
        message: response.statusText,
        data: data as T,
        httpStatus,
      };
    }

    // Validate with Zod schema if provided
    if (config?.schema) {
      const validated = config.schema.parse(data) as T;
      return {
        code: response.ok ? 0 : response.status,
        message: response.statusText,
        data: validated,
        httpStatus,
      };
    }

    // Map service-specific data key to standard 'data' key
    // NTS API is inconsistent: some use 'result', some use 'data'
    // Try the configured data key first, then fallback to alternatives
    if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      // Normalize code field (NTS sometimes returns string '0', sometimes number 0)
      const numberCode = Number(dataObj.code);
      const normalizedCode = Number.isNaN(numberCode)
        ? dataObj.code
        : numberCode;

      // Try configured data key first
      if (this.dataKey in dataObj) {
        return {
          ...dataObj,
          code: normalizedCode,
          data: dataObj[this.dataKey],
          httpStatus,
        } as ApiResponse<T>;
      }

      // Fallback: try alternative data keys for NTS API
      // NTS API is inconsistent, so we check both 'result' and 'data'
      const alternativeKeys = ['result', 'data'].filter(
        (key) => key !== this.dataKey,
      );
      for (const key of alternativeKeys) {
        if (key in dataObj) {
          return {
            ...dataObj,
            code: normalizedCode,
            data: dataObj[key],
            httpStatus,
          } as ApiResponse<T>;
        }
      }
    }

    // Return as ApiResponse (already in standard format)
    return { ...(data as ApiResponse<T>), httpStatus };
  }

  /**
   * Handle error response (code !== 0)
   */
  private async handleErrorResponse(
    apiResponse: ApiResponse<any>,
    config?: RequestConfig,
  ): Promise<void> {
    // Skip default error handler if silent mode or custom error handler is provided
    if (config?.silent || config?.onError) {
      return;
    }

    if (apiResponse.code !== 0 && this.onError) {
      const error = new ApiError(
        apiResponse.code,
        apiResponse.message || 'Request failed',
        apiResponse.data,
        apiResponse.httpStatus,
      );
      await this.onError(error);
    }
  }

  /**
   * Execute fetch request with timeout
   */
  private async executeFetch(
    url: string,
    method: 'GET' | 'POST',
    headers: Record<string, string>,
    body: string | FormData | undefined,
    timeout: number,
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      // Build fetch options - only include body for POST requests
      const fetchOptions: RequestInit = {
        method,
        headers,
        signal: controller.signal,
      };

      // Only add body for POST requests
      if (method === 'POST' && body !== undefined) {
        fetchOptions.body = body;
      }

      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Make HTTP request
   */
  protected async request<T>(
    method: 'GET' | 'POST',
    url: string,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      // Check if this is a private endpoint requiring authentication
      // If so, and no token is available, throw AuthRequiredError early
      // to prevent unnecessary API calls
      const requiresAuth = isPrivateEndpoint(url, this.service);
      if (requiresAuth && this.getToken) {
        const token = await this.getToken();
        if (!token) {
          throw new AuthRequiredError(url, this.service);
        }
      }

      // Build URL with query params (only for GET)
      const fullUrl = buildUrl(
        this.baseUrl,
        url,
        method === 'GET' ? config?.params : undefined,
      );

      // Prepare request body (only for POST)
      let body: string | FormData | undefined = undefined;
      if (method === 'POST' && config?.body) {
        // If FormData, pass directly; otherwise JSON.stringify
        body =
          config.body instanceof FormData
            ? config.body
            : JSON.stringify(config.body);
      }

      // Build headers (will auto-detect FormData from config.body)
      // Also adds sub-account header if configured and API is enabled
      const headers = await buildHeaders(
        this.defaultHeaders,
        config,
        this.getToken,
        this.subAccountConfig,
        url,
      );

      // Setup timeout
      const timeout = config?.timeout || this.defaultTimeout;

      // Execute fetch
      const response = await this.executeFetch(
        fullUrl,
        method,
        headers,
        body,
        timeout,
      );

      // Parse response
      const data = await response.json();

      // Parse and validate response
      const apiResponse = this.parseResponse<T>(response, data, config);

      // Handle error responses (pass config to check silent/onError)
      await this.handleErrorResponse(apiResponse, config);

      return apiResponse;
    } catch (error) {
      // Re-throw AuthRequiredError directly without calling onError
      // This error is expected when user is not logged in
      if (error instanceof AuthRequiredError) {
        throw error;
      }

      // Create ApiError from any error
      const apiError = createApiError(error);

      // Call onError callback (skip if silent mode or custom error handler)
      if (this.onError && !config?.silent) {
        await this.onError(apiError);
      }

      throw apiError;
    }
  }

  /**
   * GET request
   */
  async get<T>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('GET', url, config);
  }

  /**
   * POST request
   */
  async post<T>(
    url: string,
    body?: any,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>('POST', url, { ...config, body });
  }
}
